import React, { useEffect, useState } from 'react';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { Coins, Lock, Globe, Trash2, Loader2 } from 'lucide-react';
import { Navigate } from 'react-router-dom';

export default function Settings() {
  const { t, user, api, lang, refreshLibrary, refreshUser } = useApp();
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [mySongs, setMySongs] = useState([]);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    if (!user) return;
    setName(user.name || '');
    setBio(user.bio || '');
    (async () => { try { const m = await api.mine(); setMySongs(m.songs || []); } catch {} })();
    // eslint-disable-next-line
  }, [user?.id]);

  if (!user) return <Navigate to="/auth?mode=signin" replace />;

  const saveProfile = async () => {
    setSaving(true);
    try { await api.updateMe({ name, bio }); await refreshUser(); toast({ title: t('settings.saved') }); }
    catch (e) { toast({ title: 'Error', description: String(e?.response?.data?.detail || e.message) }); }
    finally { setSaving(false); }
  };

  const togglePublic = async (s) => {
    try {
      const r = await api.updateSong(s.id, { is_public: !s.isPublic });
      setMySongs(prev => prev.map(x => x.id === s.id ? r.song : x));
    } catch (e) { toast({ title: 'Error' }); }
  };

  const removeSong = async (s) => {
    if (!window.confirm(t('settings.confirm_delete'))) return;
    try {
      await api.deleteSong(s.id);
      setMySongs(prev => prev.filter(x => x.id !== s.id));
      await refreshLibrary();
    } catch (e) { toast({ title: 'Error' }); }
  };

  const buy = async (pack) => {
    setBuying(pack);
    try {
      const r = await api.buyCredits(pack);
      toast({ title: lang==='fr'?'Crédits ajoutés':'Credits added', description: `+${r.added} · ${t('settings.buy_mock')}` });
      await refreshUser();
    } catch (e) { toast({ title: 'Error' }); }
    finally { setBuying(null); }
  };

  return (
    <div className="px-4 lg:px-8 py-6 max-w-3xl mx-auto fade-up">
      <h1 className="font-display text-3xl md:text-4xl font-extrabold mb-6">{t('settings.title')}</h1>

      <section className="bg-[#101012] border border-white/5 rounded-2xl p-5 space-y-4">
        <h2 className="text-lg font-semibold">{t('settings.profile')}</h2>
        <div>
          <Label className="text-zinc-300">{t('settings.name')}</Label>
          <Input value={name} onChange={(e)=>setName(e.target.value)} className="mt-1 bg-[#16161a] border-white/10" />
        </div>
        <div>
          <Label className="text-zinc-300">{t('settings.bio')}</Label>
          <Textarea value={bio} onChange={(e)=>setBio(e.target.value)} placeholder={t('settings.bio_ph')} className="mt-1 bg-[#16161a] border-white/10 min-h-24" />
        </div>
        <Button onClick={saveProfile} disabled={saving} className="bg-white text-black hover:bg-zinc-200">
          {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null}{t('settings.save')}
        </Button>
      </section>

      <section className="bg-[#101012] border border-white/5 rounded-2xl p-5 space-y-3 mt-6">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Coins size={18} className="text-yellow-300" /> {t('settings.credits')}</h2>
        <div className="text-sm text-zinc-400">{user.credits} {lang==='fr'?'crédits disponibles':'credits available'}</div>
        <div className="flex flex-wrap gap-2 pt-1">
          {[['small', t('settings.pack_small')], ['medium', t('settings.pack_medium')], ['large', t('settings.pack_large')]].map(([k, label]) => (
            <Button key={k} onClick={()=>buy(k)} disabled={buying===k} variant="outline" className="border-white/15 bg-transparent hover:bg-white/5">
              {buying===k ? <Loader2 size={14} className="animate-spin mr-2" /> : null}{label}
            </Button>
          ))}
        </div>
        <div className="text-[11px] text-zinc-500">{t('settings.buy_mock')}</div>
      </section>

      <section className="bg-[#101012] border border-white/5 rounded-2xl p-5 mt-6">
        <h2 className="text-lg font-semibold flex items-center gap-2">{t('settings.privacy')}</h2>
        <p className="text-sm text-zinc-400 mt-1 mb-4">{t('settings.privacy_desc')}</p>
        {mySongs.length === 0 ? (
          <div className="text-sm text-zinc-500">{lang==='fr'?'Aucune chanson':'No songs'}</div>
        ) : (
          <div className="divide-y divide-white/5">
            {mySongs.map(s => (
              <div key={s.id} className="flex items-center gap-3 py-3">
                <img src={s.cover} className="w-10 h-10 rounded object-cover" alt="" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm truncate">{s.title?.[lang] || s.title?.en}</div>
                  <div className="text-xs text-zinc-500">{s.genre}</div>
                </div>
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  {s.isPublic ? <Globe size={14} className="text-emerald-400" /> : <Lock size={14} className="text-zinc-500" />}
                  <span>{s.isPublic ? t('settings.public') : t('settings.private')}</span>
                  <Switch checked={!!s.isPublic} onCheckedChange={()=>togglePublic(s)} />
                </div>
                <button onClick={()=>removeSong(s)} className="p-2 text-zinc-500 hover:text-red-400" title={t('settings.delete_song')}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
      <div className="h-24" />
    </div>
  );
}
